import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Save, Check, Building2, Globe, Mail, Download, Trash2, AlertCircle, KeyRound, Shield } from 'lucide-react';

export default function AgencySettings() {
  const { agency, refreshAgency } = useAuth();
  const [name, setName] = useState(agency?.name ?? '');
  const [email, setEmail] = useState(agency?.email ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportInfo, setExportInfo] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!agency) return;
    setSaving(true);
    setError('');

    const { error: err } = await supabase
      .from('agencies')
      .update({ name: name.trim(), email: email.trim(), updated_at: new Date().toISOString() })
      .eq('id', agency.id);

    if (err) {
      setError(err.message);
    } else {
      await refreshAgency();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    setSaving(false);
  }

  async function handleExport() {
    if (!agency) return;
    setExporting(true);
    setExportInfo('');
    setError('');

    try {
      const [appsRes, qsRes] = await Promise.all([
        supabase.from('applications').select('*').eq('agency_id', agency.id).order('submitted_at', { ascending: false }),
        supabase.from('screening_questions').select('*').eq('agency_id', agency.id).order('position'),
      ]);

      let answers: Record<string, unknown[]> = {};
      if (appsRes.data && appsRes.data.length > 0) {
        const appIds = appsRes.data.map((a) => a.id);
        const { data: ansData } = await supabase
          .from('application_answers')
          .select('*')
          .in('application_id', appIds);
        if (ansData) {
          answers = ansData.reduce((acc, ans) => {
            if (!acc[ans.application_id]) acc[ans.application_id] = [];
            acc[ans.application_id].push(ans);
            return acc;
          }, {} as Record<string, unknown[]>);
        }
      }

      const exportData = {
        agency: {
          name: agency.name,
          slug: agency.slug,
          email: agency.email,
          subscription_plan: agency.subscription_plan,
          subscription_status: agency.subscription_status,
          created_at: agency.created_at,
        },
        questions: qsRes.data ?? [],
        applications: (appsRes.data ?? []).map((app) => ({
          ...app,
          answers: answers[app.id] ?? [],
        })),
        exported_at: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `carehire-export-${agency.slug}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setExportInfo('Data exported successfully. Check your downloads folder.');
      setTimeout(() => setExportInfo(''), 4000);
    } catch {
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete() {
    if (!agency) return;
    if (deleteText !== 'DELETE') return;
    setDeleting(true);
    setError('');

    try {
      // Delete agency (cascade will remove questions, applications, answers)
      const { error: delErr } = await supabase.from('agencies').delete().eq('id', agency.id);
      if (delErr) throw delErr;

      // Sign out
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch {
      setError('Failed to delete account. Please email carehire@outlook.com for help.');
      setDeleting(false);
      setDeleteConfirm(false);
      setDeleteText('');
    }
  }

  return (
    <div className="max-w-lg space-y-8">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Agency Settings</h2>
        <p className="text-sm text-gray-500 mt-0.5">Update your agency profile.</p>
      </div>

      {/* Profile */}
      <form onSubmit={handleSave} className="space-y-5">
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <Building2 className="inline w-4 h-4 mr-1 text-gray-400" />
              Agency Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <Mail className="inline w-4 h-4 mr-1 text-gray-400" />
              Contact Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@youragency.com"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
            <p className="text-xs text-gray-400 mt-1.5">New application notifications go here.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <Globe className="inline w-4 h-4 mr-1 text-gray-400" />
              Application Link Slug
            </label>
            <div className="flex items-center gap-0 rounded-xl border border-gray-200 overflow-hidden text-sm">
              <span className="px-3 py-3 bg-gray-50 text-gray-400 border-r border-gray-200 whitespace-nowrap">
                {window.location.origin}/apply/
              </span>
              <span className="px-3 py-3 text-gray-700 font-mono">{agency?.slug}</span>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Your unique link — automatically generated when you signed up.</p>
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm">{error}</div>
        )}

        <button
          type="submit"
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-colors ${
            saved
              ? 'bg-green-500 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          } disabled:opacity-60`}
        >
          {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save changes'}
        </button>
      </form>

      {/* Data & Privacy */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Data & Privacy</h3>
        <p className="text-sm text-gray-500 mb-4">Manage your data under GDPR. Export or delete your account data.</p>

        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          {/* Data export */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Download className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Export your data</p>
                <p className="text-xs text-gray-500 mt-0.5">Download all agency data, applications, and form responses as JSON.</p>
              </div>
            </div>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-xl transition-colors disabled:opacity-60 flex-shrink-0"
            >
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
          {exportInfo && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-50 border border-green-100">
              <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">{exportInfo}</p>
            </div>
          )}

          <div className="border-t border-gray-100 pt-4">
            {/* Account deletion */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Delete account</p>
                  <p className="text-xs text-gray-500 mt-0.5">Permanently delete your agency account and all associated data. This cannot be undone.</p>
                </div>
              </div>
              <button
                onClick={() => setDeleteConfirm(!deleteConfirm)}
                className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-xl transition-colors flex-shrink-0"
              >
                Delete
              </button>
            </div>

            {deleteConfirm && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl space-y-3">
                <div className="flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">
                    This will permanently delete your agency, all applications, screening questions, and CVs.
                    This action cannot be undone. Type <strong>DELETE</strong> to confirm.
                  </p>
                </div>
                <input
                  type="text"
                  value={deleteText}
                  onChange={(e) => setDeleteText(e.target.value)}
                  placeholder="Type DELETE"
                  className="w-full px-4 py-2.5 rounded-xl border border-red-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    disabled={deleteText !== 'DELETE' || deleting}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition-colors"
                  >
                    {deleting ? 'Deleting...' : 'Permanently delete'}
                  </button>
                  <button
                    onClick={() => { setDeleteConfirm(false); setDeleteText(''); }}
                    className="px-4 py-2.5 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl border border-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Security</h3>
        <p className="text-sm text-gray-500 mb-4">Your account is protected by industry-standard security.</p>

        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">HTTPS / SSL Encryption</p>
              <p className="text-xs text-gray-500">All data is encrypted in transit and at rest.</p>
            </div>
            <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Active</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">Secure Password Storage</p>
              <p className="text-xs text-gray-500">Passwords are hashed using bcrypt — never stored in plain text.</p>
            </div>
            <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Active</span>
          </div>
        </div>
      </div>
    </div>
  );
}
